/**
 * PRP-MCP-8 work item 10: one optional question after a successful export,
 * "Did this import cleanly into Xactimate?" Yes / No / Not yet. The wording comes
 * from GET /me/import-question (app/data/import_question.json). An answer is
 * stored with the estimate, the export and the date; the page then says thanks.
 * No follow-up. Renders nothing until the question arrives or if it cannot be read.
 */
import { useEffect, useState } from 'react';

import { BackendAPI } from '../../lib/api';
import type { ImportQuestion as Question } from '../../types/exportSummary';
import { Button } from './ui/button';

export default function ImportQuestion({ parseHash, exportRef }: { parseHash: string; exportRef?: string }) {
  const [q, setQ] = useState<Question | null>(null);
  const [answered, setAnswered] = useState(false);
  useEffect(() => {
    let live = true;
    Promise.resolve()
      .then(() => BackendAPI.getImportQuestion())
      .then((r) => live && setQ(r ?? null))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);
  useEffect(() => setAnswered(false), [parseHash, exportRef]);

  if (!q) return null;
  if (answered) {
    return (
      <p className="text-[12px] text-[var(--tx-lo)]" data-testid="import-question-thanks">
        {q.thanks}
      </p>
    );
  }
  const answer = (value: string) => {
    setAnswered(true);
    void Promise.resolve()
      .then(() => BackendAPI.sendImportAnswer(parseHash, value, exportRef))
      .catch(() => {});
  };
  return (
    <div className="flex flex-wrap items-center justify-end gap-2" data-testid="import-question">
      <span className="text-[12px] text-[var(--tx-mid)]">{q.question}</span>
      {q.answers.map((a) => (
        <Button key={a.value} size="sm" onClick={() => answer(a.value)}>
          {a.label}
        </Button>
      ))}
    </div>
  );
}
